/**
 * ReadyBulkList — Tab „Bereit" der Run-Detail-Seite (T8, Plan-Zeile 137-144):
 * kompakte `ReadyRow`-Zeilen mit Stichproben-Gate, sticky Bulk-Freigabe,
 * Ergebnis-Panel (inkl. Undo-all/Push-Retry, T13/T14), Q8/E6-Abschlusszustand
 * bei 0 pending. Zustand/Bulk-Logik ausgelagert in
 * `useReadyRowState`/`useApproveAllSuggestions`. Produktname via
 * `useProductNames` (T15/P2). `ApplyResultDialog` (T13/P4) öffnet einmalig
 * automatisch bei neuem `result` — das inline `ReadyResultPanel` bleibt als
 * Tab-Zustand nach dem Schließen bestehen.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox } from 'lucide-react'

import { EmptyState } from '@/components/v2/ui'
import { useChannels } from '@/hooks/useChannels'

import { useSuggestions } from '../hooks/useSuggestions'
import { useTextRunMutations } from '../hooks/useSuggestionMutations'
import { useProductNames } from '../hooks/useProductNames'
import { useReadyRowState } from './useReadyRowState'
import { useApproveAllSuggestions } from './useApproveAllSuggestions'
import { ReadyRowsSection } from './ReadyRowsSection'
import { ReadyBulkBar } from './ReadyBulkBar'
import { ReadyResultPanel } from './ReadyResultPanel'
import { ApplyResultDialog } from './ApplyResultDialog'
import { DiscardDialog } from './DiscardDialog'
import { isGateOpen } from './sampleGate'

const PAGE_SIZE = 20

export function ReadyBulkList({ runId, worklistTo }: { runId: string; worklistTo?: string | null }) {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [discardingId, setDiscardingId] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const { openIds, everSeenIds, toggleRow } = useReadyRowState()

  const { data, isLoading } = useSuggestions({ run_id: runId, route: 'auto_safe', status: 'pending', page })
  const { apply, discard } = useTextRunMutations()
  const { data: channels } = useChannels()
  const { result, appliedIds, approving, handleApproveAll } = useApproveAllSuggestions(runId, apply.mutateAsync)
  useEffect(() => {
    if (result) setDialogOpen(true)
  }, [result])

  const channelNameById: Record<string, string> = {}
  for (const c of channels ?? []) channelNameById[c.id] = c.custom_name || c.channel_name

  const items = data?.items ?? []
  const { data: productNames } = useProductNames(items.map((s) => s.product_id))
  const productLabelById: Record<string, string> = {}
  for (const s of items) productLabelById[s.product_id] = productNames?.products[s.product_id] || s.product_id.slice(0, 8)

  const totalPages = page + (items.length === PAGE_SIZE ? 1 : 0)
  const gateOpen = isGateOpen(everSeenIds.size, items.length)
  const isBusy = (id: string) =>
    approving ||
    (apply.isPending && apply.variables?.suggestion_ids.includes(id) === true) ||
    (discard.isPending && discardingId === id)

  if (isLoading && !result) return null

  return (
    <div className="text-bulk-ready-list space-y-4 pb-20">
      {result && <ReadyResultPanel result={result} appliedIds={appliedIds} />}
      {result && (
        <ApplyResultDialog
          result={result}
          appliedIds={appliedIds}
          channelNameById={channelNameById}
          worklistTo={worklistTo ?? null}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />
      )}
      {items.length === 0 ? (
        <EmptyState icon={<Inbox className="h-12 w-12" />} title={t('channelContent.ready.allReviewed')} />
      ) : (
        <>
          <ReadyRowsSection
            items={items}
            channelNameById={channelNameById}
            productLabelById={productLabelById}
            everSeenIds={everSeenIds}
            openIds={openIds}
            isBusy={isBusy}
            onToggle={toggleRow}
            onApplyRow={(id, editedValue) => apply.mutate({ suggestion_ids: [id], edited_value: editedValue })}
            onDiscardRow={setDiscardingId}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
          <ReadyBulkBar count={items.length} gateOpen={gateOpen} approving={approving} onApproveAll={handleApproveAll} />
        </>
      )}

      <DiscardDialog
        open={discardingId !== null}
        busy={discard.isPending}
        onCancel={() => setDiscardingId(null)}
        onConfirm={(reason) => {
          if (!discardingId) return
          discard.mutate({ suggestion_ids: [discardingId], reason })
          setDiscardingId(null)
        }}
      />
    </div>
  )
}
